import React from "react";
import styled from "styled-components";
import { IconSvg } from "monica-alexandria";
import { Timeline_Comment } from "./Timeline_Comment";
import { Timeline_Status } from "./Timeline_Status";
import { Timeline_Calendar } from "./Timeline_Calendar";
import { Timeline_Seller } from "./Timeline_Seller";
import { Timeline_Brand_Update } from "./Timeline_Brand_Update";
import { Timeline_Client_Link } from "./Timeline_Client_Link";
import { Timeline_Sales_Session } from "./salesSession/Timeline_Sales_Session";
import { Timeline_Meeting_Session } from "./meetingSession/Timeline_Meeting_Session";
import { Timeline_Brand } from "./Timeline_Brand";
import { Timeline_Offer } from "./Timeline_Offer";
import { Timeline_Sass } from "./Timeline_Sass";
import { Timeline_Communication } from "./Timeline_Communication";
import { Timeline_Work_Completed } from "./Timeline_Work_Completed";
import { Timeline_Merge } from "./Timeline_Merge";
import { Timeline_Tag } from "./Timeline_Tag";
import { Timeline_System } from "./Timeline_System";
import { Timeline_Brand_Created } from "./Timeline_Brand_Created";
import { Timeline_Legacy_Activity } from "./legacyActivity/Timeline_Legacy_Activity";
import {
    read_timeline_actor_label,
    read_timeline_date_label,
    read_timeline_source_label,
    read_timeline_title
} from "../../utils/timeline/timeline_formatting";

const timelineAccentLookup = {
    BRAND_CREATED: "var(--success)",
    COMMENT: "var(--main)",
    TO_CALL: "var(--main)",
    IN_PROGRESS: "var(--focus)",
    STALLING: "var(--warning)",
    LOST: "var(--error)",
    SELLER_ASSIGNED: "var(--focus)",
    SELLER_REMOVED: "var(--warning)",
    BRAND_UPDATE: "var(--focus)",
    OWNER_REGISTERED: "var(--success)",
    SALES_SESSION: "var(--main)",
    SASS_SESSION: "var(--main)",
    BRAND_ARCHIVED: "var(--warning)",
    CLIENT_CREATED: "var(--success)",
    CLIENT_LINKED: "var(--success)",
    CLIENT_UNLINKED: "var(--warning)",
    OFFER_CREATED: "var(--main)",
    OFFER_PAID: "var(--success)",
    OFFER_ACCEPTED: "var(--success)",
    SASS: "var(--main)",
    SASS_SET: "var(--warning)",
    SASS_RESCHEDULE: "var(--warning)",
    SASS_IN_PROGRESS: "var(--focus)",
    SASS_LOST: "var(--error)",
    SASS_PAID: "var(--success)",
    COMMUNICATION: "var(--main)",
    WORK_COMPLETED: "var(--success)",
    PROSPECT_MERGED: "var(--focus)",
    BRAND_MERGED: "var(--focus)",
    TAG: "var(--main)",
    SYNC_WARNING: "var(--warning)",
    SYNC_RECOVERED: "var(--success)",
    LEGACY_EVENT: "var(--focus)",
    LEGACY_ACTIVITY: "var(--focus)",
    GENERIC: "var(--focus)"
};

const timelineIconLookup = {
    BRAND_CREATED: "Add",
    COMMENT: "FbComment",
    TO_CALL: "Phone",
    IN_PROGRESS: "Play",
    STALLING: "Calendar",
    LOST: "Flag",
    SELLER_ASSIGNED: "AddUser",
    SELLER_REMOVED: "User",
    BRAND_UPDATE: "Edit",
    OWNER_REGISTERED: "AddUser",
    SALES_SESSION: "Budget",
    SASS_SESSION: "Rocket",
    BRAND_ARCHIVED: "Recycle",
    CLIENT_CREATED: "AddUser",
    CLIENT_LINKED: "FbLink",
    CLIENT_UNLINKED: "Xicon",
    OFFER_CREATED: "Coupon",
    OFFER_PAID: "Payment",
    OFFER_ACCEPTED: "Check",
    SASS: "Rocket",
    SASS_SET: "Calendar",
    SASS_RESCHEDULE: "History",
    SASS_IN_PROGRESS: "Play",
    SASS_LOST: "Flag",
    SASS_PAID: "Payment",
    COMMUNICATION: "Email",
    WORK_COMPLETED: "Report",
    PROSPECT_MERGED: "Group",
    BRAND_MERGED: "Group",
    TAG: "Tag",
    SYNC_WARNING: "Warning",
    SYNC_RECOVERED: "Check",
    LEGACY_EVENT: "History",
    LEGACY_ACTIVITY: "History",
    GENERIC: "Generic"
};

const timelineRendererLookup = {
    BRAND_CREATED: Timeline_Brand_Created,
    COMMENT: Timeline_Comment,
    TO_CALL: Timeline_Status,
    IN_PROGRESS: Timeline_Status,
    STALLING: Timeline_Calendar,
    LOST: Timeline_Status,
    SELLER_ASSIGNED: Timeline_Seller,
    SELLER_REMOVED: Timeline_Seller,
    BRAND_UPDATE: Timeline_Brand_Update,
    OWNER_REGISTERED: Timeline_Brand,
    SALES_SESSION: Timeline_Sales_Session,
    SASS_SESSION: Timeline_Meeting_Session,
    BRAND_ARCHIVED: Timeline_Brand,
    CLIENT_CREATED: Timeline_Client_Link,
    CLIENT_LINKED: Timeline_Client_Link,
    CLIENT_UNLINKED: Timeline_Client_Link,
    OFFER_CREATED: Timeline_Offer,
    OFFER_PAID: Timeline_Offer,
    OFFER_ACCEPTED: Timeline_Offer,
    SASS: Timeline_Sass,
    SASS_SET: Timeline_Sass,
    SASS_RESCHEDULE: Timeline_Sass,
    SASS_IN_PROGRESS: Timeline_Sass,
    SASS_LOST: Timeline_Sass,
    SASS_PAID: Timeline_Sass,
    COMMUNICATION: Timeline_Communication,
    WORK_COMPLETED: Timeline_Work_Completed,
    PROSPECT_MERGED: Timeline_Merge,
    BRAND_MERGED: Timeline_Merge,
    TAG: Timeline_Tag,
    SYNC_WARNING: Timeline_System,
    SYNC_RECOVERED: Timeline_System,
    LEGACY_EVENT: Timeline_System,
    LEGACY_ACTIVITY: Timeline_Legacy_Activity,
    GENERIC: Timeline_System
};

const TimelineItem = styled.article`
    display: grid;
    grid-template-columns: 4.4rem minmax(0, 1fr);
    gap: 1.2rem;
    min-width: 0;
`;

const TimelineRail = styled.div`
    position: relative;
    display: flex;
    justify-content: center;

    &::after {
        content: "";
        position: absolute;
        top: 3.6rem;
        bottom: -1.6rem;
        width: 0.1rem;
        background: ${p => p.theme.low};
    }
`;

const TimelineIcon = styled.div`
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 50%;
    color: ${p => p.eventType === "SALES_SESSION" ? p.theme.color : timelineAccentLookup[p.eventType]};
    background: ${p => p.theme.background};
    box-shadow: ${p => p.theme.out};

    svg {
        width: 1.8rem;
        height: 1.8rem;
    }
`;

const TimelineBody = styled.div`
    display: grid;
    gap: 0.6rem;
    min-width: 0;
    padding: 0 0 1.6rem;
    border-bottom: 0.1rem solid ${p => p.theme.low};
`;

const TimelineEvents = styled.div`
    display: grid;
    gap: 0.6rem;
    min-width: 0;
`;

const TimelineEvent = styled.div`
    display: grid;
    gap: 0.4rem;
    min-width: 0;

    & + & {
        padding-top: 0.6rem;
        border-top: 0.1rem solid ${p => p.theme.low};
    }
`;

const TimelineHeading = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;

    h5 {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
    }
`;

const TimelineSources = styled.div`
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
`;

const TimelineSource = styled.p`
    &::before {
        content: "·";
        margin-right: 0.6rem;
    }
`;

const TimelineMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem 1rem;
    flex-wrap: wrap;

    p {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
    }
`;

// Displays a concise title only for structural events.
const Timeline_Event_Heading = (props) => {
    return (
        <TimelineHeading>
            <h5>{read_timeline_title(props.event, props.t)}</h5>
        </TimelineHeading>
    );
};

// Leaves comments as body text without a redundant event title.
const Timeline_Event_Heading_Empty = () => {
    return null;
};

const timelineHeadingLookup = {
    BRAND_CREATED: Timeline_Event_Heading_Empty,
    COMMENT: Timeline_Event_Heading_Empty,
    COMMUNICATION: Timeline_Event_Heading_Empty,
    WORK_COMPLETED: Timeline_Event_Heading_Empty,
    CLIENT_CREATED: Timeline_Event_Heading_Empty,
    CLIENT_LINKED: Timeline_Event_Heading_Empty,
    CLIENT_UNLINKED: Timeline_Event_Heading_Empty,
    SALES_SESSION: Timeline_Event_Heading_Empty,
    SASS_SESSION: Timeline_Event_Heading_Empty,
    LEGACY_ACTIVITY: Timeline_Event_Heading_Empty
};
// Reads the renderer associated with one canonical event type.
const read_timeline_renderer = (type) => {
    return timelineRendererLookup[type] || Timeline_System;
};

// Reads the heading renderer associated with one canonical event type.
const read_timeline_heading_renderer = (type) => {
    return timelineHeadingLookup[type] || Timeline_Event_Heading;
};

// Reads the icon associated with one canonical event type.
const read_timeline_icon = (type) => {
    return timelineIconLookup[type] || timelineIconLookup.GENERIC;
};

// Reads each production source label once for the grouped metadata row.
const read_timeline_group_source_labels = (group, t) => {
    return [...new Set((group?.events || []).map((event) => {
        return read_timeline_source_label(event?.sourceApp, t, event?.type);
    }).filter(Boolean))];
};

// Displays one canonical event inside its grouped audit block.
const Timeline_Event = (props) => {
    const TimelineRenderer = read_timeline_renderer(props.event?.type);
    const TimelineHeadingRenderer = read_timeline_heading_renderer(props.event?.type);

    return (
        <TimelineEvent data-testid={`timeline-event-${props.event?.type}`}>
            <TimelineHeadingRenderer event={props.event} t={props.t} />
            <TimelineRenderer event={props.event} metadataEvent={props.metadataEvent} teamMembers={props.teamMembers} t={props.t} />
            {props.event?.data?.conversion?.ref && !["OFFER_PAID", "OFFER_ACCEPTED"].includes(props.event?.type) ? (
                <Timeline_Offer event={{ data: { ref: props.event.data.conversion.ref } }} t={props.t}></Timeline_Offer>
            ) : null}
            {props.event?.data?.conversion?.dif ? (
                <Timeline_Client_Link event={{ data: { dif: props.event.data.conversion.dif } }} t={props.t}></Timeline_Client_Link>
            ) : null}
        </TimelineEvent>
    );
};

// Displays one actor-and-minute event group on the connected audit rail.
export default function Timeline_Item(props) {
    const primaryEvent = props.group?.events?.[0];
    const usesStandaloneCard = ["BRAND_CREATED", "SALES_SESSION", "SASS_SESSION", "LEGACY_ACTIVITY"].includes(primaryEvent?.type);

    return (
        <TimelineItem data-testid="timeline-group">
            <TimelineRail>
                <TimelineIcon eventType={primaryEvent?.type} title={read_timeline_title(primaryEvent, props.t)}>
                    <IconSvg Icon={read_timeline_icon(primaryEvent?.type)} />
                </TimelineIcon>
            </TimelineRail>
            <TimelineBody>
                {!usesStandaloneCard && (
                    <TimelineMeta>
                        <p>{read_timeline_actor_label(primaryEvent, props.t)}</p>
                        <TimelineSources>
                            <p>{read_timeline_date_label(props.group?.changedAt) || props.t("Date unavailable")}</p>
                            {read_timeline_group_source_labels(props.group, props.t).map((sourceLabel) => (
                                <TimelineSource key={sourceLabel}>{sourceLabel}</TimelineSource>
                            ))}
                        </TimelineSources>
                    </TimelineMeta>
                )}
                <TimelineEvents>
                    {props.group?.events?.map((event) => (
                        <Timeline_Event key={event.eventId || event.sourceKey} event={event} metadataEvent={{ by: primaryEvent?.by, changedAt: props.group?.changedAt }} teamMembers={props.teamMembers} t={props.t} />
                    ))}
                </TimelineEvents>
            </TimelineBody>
        </TimelineItem>
    );
};

export { Timeline_Item };
